/**
 * System templates for completeness evaluation.
 * First template: portable-livescope-ice.
 * Boat / hybrid templates are not seeded in this ticket.
 */

const { SYSTEM_ROLES } = require('./productCatalog');

const REQUIREMENT_LEVELS = Object.freeze([
  'REQUIRED',
  'CONDITIONAL',
  'RECOMMENDED',
  'OPTIONAL',
]);

const COMPLETENESS_SCOPES = Object.freeze(['ELECTRONICS', 'ICE_READY', 'SYSTEM']);

const TEMPLATE_STATUSES = Object.freeze(['ACTIVE', 'INACTIVE']);

const PORTABLE_LIVESCOPE_ICE_SLUG = 'portable-livescope-ice';

const portableLivescopeIceTemplate = Object.freeze({
  slug: PORTABLE_LIVESCOPE_ICE_SLUG,
  name: 'Portable LiveScope Ice System',
  description:
    'Functionally complete portable Garmin LiveScope ice-fishing setup. Determines missing roles; does not choose products.',
  status: 'ACTIVE',
  roles: Object.freeze([
    {
      role: 'LIVE_SONAR',
      requirementLevel: 'REQUIRED',
      completenessScope: 'ELECTRONICS',
      sortOrder: 10,
      notes: 'Required live sonar transducer or LiveScope 2 unit.',
    },
    {
      role: 'DISPLAY',
      requirementLevel: 'REQUIRED',
      completenessScope: 'ELECTRONICS',
      sortOrder: 20,
      notes: 'Required display. Role population is not sufficient if compatibility is INCOMPATIBLE.',
    },
    {
      role: 'SONAR_PROCESSOR_INTERFACE',
      requirementLevel: 'REQUIRED',
      completenessScope: 'ELECTRONICS',
      sortOrder: 30,
      notes: 'Satisfied by GLS 10 or by LiveScope 2 products that include onboard processing. Packages may satisfy this role.',
    },
    {
      role: 'POWER_SOURCE',
      requirementLevel: 'REQUIRED',
      completenessScope: 'ICE_READY',
      sortOrder: 40,
      notes: 'Required for Ice-Ready. Any approved battery satisfies the role; runtime is out of scope.',
    },
    {
      role: 'PORTABLE_PLATFORM',
      requirementLevel: 'REQUIRED',
      completenessScope: 'ICE_READY',
      sortOrder: 50,
      notes: 'Required for Ice-Ready (e.g. approved shuttle). Do not auto-select a shuttle.',
    },
    {
      role: 'TRANSDUCER_POSITIONING',
      requirementLevel: 'REQUIRED',
      completenessScope: 'ICE_READY',
      sortOrder: 60,
      notes: 'Required for Ice-Ready (e.g. pole). Role satisfaction does not prove pole/transducer mounting compatibility.',
    },
    {
      role: 'CONNECTIVITY',
      requirementLevel: 'CONDITIONAL',
      completenessScope: 'SYSTEM',
      conditionData: { enforceWhen: 'VERIFIED_REQUIREMENT' },
      sortOrder: 70,
      notes: 'Only enforce when a verified compatibility requirement exists. Do not invent cables.',
    },
    {
      role: 'TRANSDUCER_ADAPTER',
      requirementLevel: 'CONDITIONAL',
      completenessScope: 'SYSTEM',
      conditionData: { enforceWhen: 'VERIFIED_REQUIRES_ADAPTER' },
      sortOrder: 80,
      notes: 'Only required when a VERIFIED REQUIRES_ADAPTER rule exists for the selected combination.',
    },
    {
      role: 'DISPLAY_MOUNTING',
      requirementLevel: 'CONDITIONAL',
      completenessScope: 'SYSTEM',
      conditionData: { enforceWhen: 'EXPLICIT_REQUIRED_HARDWARE' },
      sortOrder: 90,
      notes: 'Do not fail MVP builds because Garmin bail brackets are not yet modeled.',
    },
    {
      role: 'CHARGER',
      requirementLevel: 'RECOMMENDED',
      completenessScope: 'SYSTEM',
      sortOrder: 100,
      notes: 'Recommended. Missing charger does not block electronics or Ice-Ready completeness.',
    },
    {
      role: 'PROTECTION',
      requirementLevel: 'OPTIONAL',
      completenessScope: 'SYSTEM',
      sortOrder: 110,
      notes: 'Optional bag/cover. Does not block completeness.',
    },
  ]),
});

const systemTemplates = Object.freeze([portableLivescopeIceTemplate]);

function getSystemTemplate(slug) {
  return systemTemplates.find((template) => template.slug === slug) || null;
}

function validateSystemTemplates(templates = systemTemplates) {
  const errors = [];
  const slugs = new Set();
  for (const template of templates) {
    if (!template?.slug) errors.push('template missing slug');
    else if (slugs.has(template.slug)) errors.push(`duplicate template slug ${template.slug}`);
    else slugs.add(template.slug);
    if (!template?.name) errors.push(`${template?.slug} missing name`);
    if (!TEMPLATE_STATUSES.includes(template.status)) {
      errors.push(`${template.slug}: invalid status ${template.status}`);
    }
    const roles = template.roles || [];
    const roleNames = new Set();
    for (const row of roles) {
      if (!SYSTEM_ROLES.includes(row.role)) {
        errors.push(`${template.slug}: unknown role ${row.role}`);
      }
      if (roleNames.has(row.role)) errors.push(`${template.slug}: duplicate role ${row.role}`);
      roleNames.add(row.role);
      if (!REQUIREMENT_LEVELS.includes(row.requirementLevel)) {
        errors.push(`${template.slug}.${row.role}: invalid requirementLevel`);
      }
      if (!COMPLETENESS_SCOPES.includes(row.completenessScope)) {
        errors.push(`${template.slug}.${row.role}: invalid completenessScope`);
      }
    }
  }
  if (!slugs.has(PORTABLE_LIVESCOPE_ICE_SLUG)) {
    errors.push('portable-livescope-ice template is required');
  }
  return errors;
}

module.exports = {
  REQUIREMENT_LEVELS,
  COMPLETENESS_SCOPES,
  TEMPLATE_STATUSES,
  PORTABLE_LIVESCOPE_ICE_SLUG,
  portableLivescopeIceTemplate,
  systemTemplates,
  getSystemTemplate,
  validateSystemTemplates,
};
